import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  X,
  FileText,
  Tag,
  Calendar,
  FolderTree,
  Shield,
  User,
  Download,
  Link2,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
} from "lucide-react";
import { api } from "@/api/client";
import {
  CONFIDENTIALITY_LABELS,
  CONFIDENTIALITY_COLORS,
  STATUS_COLORS,
} from "@/types/document";
import type { Document, DocumentVersion, DocumentStatus, ConfidentialityLevel } from "@/types/document";

interface DocumentDetailModalProps {
  documentId: string;
  onClose: () => void;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export default function DocumentDetailModal({ documentId, onClose }: DocumentDetailModalProps) {
  const navigate = useNavigate();

  const { data: doc, isLoading } = useQuery<Document>({
    queryKey: ["documents", documentId],
    queryFn: () => api.get(`/documents/${documentId}`),
  });

  const { data: versions = [] } = useQuery<DocumentVersion[]>({
    queryKey: ["documents", documentId, "versions"],
    queryFn: () => api.get(`/documents/${documentId}/versions`),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <h2 className="text-sm font-bold text-gray-900 truncate">
              DETAIL DOKUMEN{doc?.nomor_dokumen ? `: ${doc.nomor_dokumen}` : ""}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-gray-100 transition-colors"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
            </div>
          ) : doc ? (
            <div className="divide-y divide-gray-100">
              {/* Basic info */}
              <div className="px-5 py-4 space-y-2.5">
                <div className="flex items-start gap-2">
                  <FileText className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400">Judul</p>
                    <p className="text-sm font-medium text-gray-900">{doc.judul}</p>
                  </div>
                </div>

                {doc.nomor_dokumen && (
                  <div className="flex items-start gap-2">
                    <Tag className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-400">Nomor</p>
                      <p className="text-sm font-mono text-blue-700">{doc.nomor_dokumen}</p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400">Tanggal</p>
                    <p className="text-sm text-gray-700">
                      Terbit: {formatDate(doc.tanggal_dokumen)}
                    </p>
                    {(doc.tanggal_berlaku || doc.tanggal_berakhir) && (
                      <p className="text-sm text-gray-700">
                        Berlaku: {formatDate(doc.tanggal_berlaku)} — {formatDate(doc.tanggal_berakhir)}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Taksonomi Path */}
              <div className="px-5 py-4">
                <div className="flex items-start gap-2">
                  <FolderTree className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-400">Taksonomi</p>
                    <p className="text-sm font-mono text-indigo-700 bg-indigo-50 rounded px-2 py-1 mt-0.5 inline-block">
                      /PNBP/{doc.jenis_pnbp_kode}_{doc.jenis_pnbp_nama}/{doc.siklus_keuangan_kode}_{doc.siklus_keuangan_nama}/{doc.bentuk_dokumen_kode}_{doc.bentuk_dokumen_nama}/
                    </p>
                  </div>
                </div>
              </div>

              {/* Tags & Status */}
              <div className="px-5 py-4 space-y-2.5">
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className={`inline-flex px-2.5 py-1 rounded text-xs font-medium ${
                      STATUS_COLORS[doc.status_kode as DocumentStatus] || "bg-gray-100"
                    }`}
                  >
                    {doc.status_nama}
                  </span>
                  <span
                    className={`inline-flex px-2.5 py-1 rounded text-xs font-medium ${
                      CONFIDENTIALITY_COLORS[doc.tingkat_kerahasiaan as ConfidentialityLevel] || "bg-gray-100"
                    }`}
                  >
                    <Shield className="h-3 w-3 mr-1" />
                    {CONFIDENTIALITY_LABELS[doc.tingkat_kerahasiaan as ConfidentialityLevel] || doc.tingkat_kerahasiaan}
                  </span>
                </div>

                {doc.tags.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Tag className="h-3 w-3 text-gray-400" />
                    {[...new Map(doc.tags.map((t) => [t.id, t])).values()].map((tag) => (
                      <span key={tag.id} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">
                        #{tag.nama}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Deskripsi */}
              {doc.deskripsi && (
                <div className="px-5 py-4">
                  <p className="text-xs text-gray-400 mb-1">Deskripsi</p>
                  <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                    {doc.deskripsi}
                  </div>
                </div>
              )}

              {/* PIC & Metadata */}
              <div className="px-5 py-4 space-y-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <span className="text-xs text-gray-400">Dibuat oleh:</span>
                  <span className="text-sm font-medium text-gray-700">{doc.created_by_username}</span>
                </div>
                {doc.file_name && (
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gray-400" />
                    <span className="text-xs text-gray-400">File:</span>
                    <span className="text-sm text-gray-700">{doc.file_name}</span>
                    <span className="text-xs text-gray-400">({formatFileSize(doc.file_size)})</span>
                  </div>
                )}
              </div>

              {/* Riwayat Versi */}
              {versions.length > 0 && (
                <div className="px-5 py-4">
                  <p className="text-xs text-gray-400 mb-2">Riwayat Versi</p>
                  <div className="space-y-1.5">
                    {versions.map((v) => (
                      <div key={v.id} className="flex items-center gap-2 text-xs">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <span className="font-mono font-bold text-gray-600">v{v.versi_ke}</span>
                        <span className="text-gray-500">({formatDate(v.uploaded_at)})</span>
                        <span className="text-gray-400">—</span>
                        <span className="text-gray-600">{v.file_name}</span>
                        {v.uploaded_by_username && (
                          <span className="text-gray-400">oleh {v.uploaded_by_username}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="px-5 py-16 text-center text-sm text-gray-400">
              Dokumen tidak ditemukan
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            {doc?.file_name && (
              <button
                onClick={() => window.open(`/api/v1/documents/${documentId}/download`, "_blank")}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-white transition-colors"
              >
                <Download className="h-3.5 w-3.5" />
                Download
              </button>
            )}
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.origin + `/documents/${documentId}`);
              }}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-white transition-colors"
            >
              <Link2 className="h-3.5 w-3.5" />
              Salin Link
            </button>
          </div>
          <button
            onClick={() => {
              onClose();
              navigate(`/documents/${documentId}`);
            }}
            className="rounded-lg bg-blue-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-blue-700 transition-colors"
          >
            Buka Detail Lengkap
          </button>
        </div>
      </div>
    </div>
  );
}
